import React, { useEffect, useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import InfoRow from '@vlabs/shared/components/info-row/InfoRow';
import { parseISOToLocale } from '@vlabs/shared/utils';
import { CopyButton, Panel, Control, Tag, Divider, useWindowSize } from '@vlabs/uikit';
import { Map, Marker } from 'pigeon-maps';
import { osm } from 'pigeon-maps/providers';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { routes } from '@vlapps/point/src/app/routes';

import st from './InfoBlock.module.sass';

const CopyText = ({ text }) => {
  const { t } = useTranslation();

  return (
    <div className={st.CopyTextWrapper}>
      <span className={st.CopyText__Ellipsis}>{text}</span>
      <div className={st.CopyText__Button}>
        <CopyButton title={t('copy_label')} value={text} />
      </div>
    </div>
  );
};

export const InfoBlock = ({ event, extraTags }) => {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const [currentStream, setCurrentStream] = useState(null);
  const location = event?.event?.location;
  const hasLocation = location && Object.values(location).some((val) => val != null && val !== '');

  const latitude = location?.geo_position?.latitude;
  const longitude = location?.geo_position?.longitude;
  const hasCoordinates = latitude != null && longitude != null;

  useEffect(() => {
    if (!event?.event?.stream_id) return;

    const fetchStream = async () => {
      try {
        const data = await apiContainer.lunaClient.streams.get(event.event.stream_id);
        setCurrentStream({ name: data.name || data.stream_id, stream_id: data.stream_id });
      } catch {
        setCurrentStream({ isDeleted: true });
      }
    };

    fetchStream();
  }, [event?.event?.stream_id]);

  const streamLink = event?.event?.stream_id
    && generatePath(routes.videoAnalytics.streams.edit, { streamId: event?.event?.stream_id });

  const streamValue = currentStream?.isDeleted ? <CopyText text={event?.event?.stream_id} /> : (
    <Control.Link
      className={st.Link}
      data-testid={`CamLink-${currentStream?.stream_id}`}
      target="_blank"
      to={streamLink}
    >
      {currentStream?.name}
    </Control.Link>
  );

  return (
    <Panel className={st.VideoAnalyticDetails__Panel} data-testid='VADetailsPanel'>
      <div className={st.Subtitle} data-testid='InfoBlockSubtitle'>{t('videoAnalytics:details.info block.common info.label')}</div>
      <Divider small />

      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('videoAnalytics:event_type.title')}
        value={(
          <div className={st.TagsWrapper}>
            <Tag kind="neutral" size={windowSize.width < 1920 ? 'medium' : 'large'} value={t(`videoAnalytics:archivePage.infoCard.tag.${event?.event_type}`)} />
            {extraTags}
          </div>
        )}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('date created')}
        value={(
          <div className={st.TimeRangeWrapper}>
            <span>{`${parseISOToLocale(event?.create_time)} -`}</span>
            <br />
            <span>{`${parseISOToLocale(event?.end_time)}`}</span>
          </div>
        )}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('event_id_label')}
        value={<CopyText text={event?.event?.event_id} />}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('track_id_label')}
        value={<CopyText text={event?.event?.track_id} />}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('source')}
        value={streamValue}
      />
      <div className={st.Divider} />

      <div className={st.Subtitle} data-testid='InfoBlockSubtitle'>
        {t('videoAnalytics:details.info block.geolocation.label')}
      </div>
      {!hasLocation && <div className={st.EmptyInfo}>{t('no data.title')}</div>}
      <Divider small />
      {hasLocation && hasCoordinates && (
        <div className={st.Map}>
          <Map
            attribution={false}
            center={[+latitude, +longitude]}
            defaultZoom={18}
            mouseEvents={false}
            provider={osm}
            touchEvents={false}
          >
            <Marker
              anchor={[+latitude, +longitude]}
              color="var(--color-primary)"
            />
          </Map>
        </div>
      )}
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.city')}
        value={event?.event?.location?.city}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.area')}
        value={event?.event?.location?.area}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.district')}
        value={event?.event?.location?.district}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.street')}
        value={event?.event?.location?.street}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.house_number')}
        value={event?.event?.location?.house_number}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.latitude')}
        value={latitude}
      />
      <InfoRow
        className={st.VideoAnalyticDetails__InfoRow}
        data-testid='InfoBlock'
        title={t('geoposition.longitude')}
        value={longitude}
      />
    </Panel>
  );
};
